import { UserServiceClient } from 'fitbeat.api-clients';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import {
  ActivityIndicator, Image, View, Platform, StyleSheet, ScrollView, Text,
} from 'react-native';
import Theme, { ThemeType } from '../theme';
import { Logger } from '../utils/Logger';
import { PrivacyList, Styles } from './components';
import { UserDataConsumer, isWeb } from '../utils';
import {
  BaseModal, showError, SearchPartner, ConfirmationModal,
} from '../ui';
import {
  partnerBlocked, partnerAdded, partnerRemoved, partnerUnblocked,
} from '../ui/components/Toast';
import {
  showToastMessage,
  toggleWorkoutPartnersModal,
  togglePrivacyRemoveConfirmationModalVisible,
} from '../ui/redux/uiActions';
import {
  Button, ButtonTypes, Heading, HeadingTypes,
} from '../components';
import { defaultScale, scaleImageAspectRatio } from '../utils/scaling';
import { shareOpenInviteLink } from '../Bookings/utils';
import { getApiConfig } from '../config';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { shareInvite } from '../Bookings/actions';

class AllowedPartners extends Component {
  constructor(props) {
    super(props);

    const {
      user: {
        userProfile: {
          partnerWhitelist = [],
        },
      },
    } = this.props;
    partnerWhitelist.forEach((partner) => {
      partner.listType = 'whitelist';
    });

    this.state = {
      isLoading: false,
      isSearchLoading: false,
      partnerIdentifier: '',
      partnerToRemove: null,
      listType: 'whitelist',
      searchedPartners: partnerWhitelist,
      copyLinkMessage: this.props.t('inviteFriend'),
    };
  }

  // TODO: Move this higher up for all error handling
  handelError = (error, caller = '') => {
    const { user: { userProfile } } = this.props;
    console.log('error', error);
    showError(error.message);
    Logger.notify(error, (report) => {
      report.errorClass = 'WorkoutPartners';
      let userDetails = '';
      if (userProfile) {
        userDetails = `User details:\n
                    id:${userProfile.uid},
                    email:${userProfile.email},
                    name:${userProfile.fullname}`;
      }
      report.errorMessage = `Caller: ${caller}\n\nuserDetails: ${userDetails}`;
    });
  };

  setIsLoading = (isLoading) => {
    this.setState({ isLoading });
  };

  setIsSeachLoading = (isSearchLoading) => {
    this.setState({ isSearchLoading });
  };

  block = async (partner) => {
    const { user: { userProfileService: { blockToBlacklist } }, t } = this.props;
    try {
      this.props.setIsFetching(true);
      await blockToBlacklist(partner.uid);
      this.props.setSuccessOrFailMessage(t('blockFriendSuccess'));
    } catch (error) {
      this.props.setSuccessOrFailMessage(error);
      this.handelError(error, 'block');
    } finally {
      this.props.setIsFetching(false);
    }
  };

  unblock = async (partner) => {
    const { user: { userProfileService: { unblockFromBlacklist } }, t } = this.props;
    try {
      this.props.setIsFetching(true);
      await unblockFromBlacklist(partner.uid);
      this.props.setSuccessOrFailMessage(t('unblockFriendSuccess'));
    } catch (error) {
      this.handelError(error, 'unblock');
      this.props.setSuccessOrFailMessage(error);
    } finally {
      this.props.setIsFetching(false);
    }
  };

  remove = async (partner) => {
    const { user: { userProfileService: { removeFromWhitelist } }, t } = this.props;
    try {
      this.props.setIsFetching(true);
      await removeFromWhitelist(partner.uid);
      this.props.setSuccessOrFailMessage(t('removeFriendSuccess'));
    } catch (error) {
      this.props.setSuccessOrFailMessage(error);
      this.handelError(error, 'remove');
    } finally {
      this.props.setIsFetching(false);
    }
  };

  onSearch = async (partnerIdentifier) => {
    const {
      user: {
        userProfile: { partnerBlacklist = [], partnerWhitelist = [] }, userProfileService: { searchPrivacyPartners },
      },
    } = this.props;
    try {
      this.setIsSeachLoading(true);
      const searchedPartners = await searchPrivacyPartners(partnerIdentifier);
      searchedPartners.forEach((searchPartner) => {
        const foundBlacklist = partnerBlacklist.find((blacklistPartner) => blacklistPartner.uid === searchPartner.uid);
        const foundWhitelist = partnerWhitelist.find((whitelistPartner) => whitelistPartner.uid === searchPartner.uid);
        if (foundBlacklist) {
          searchPartner.listType = 'blacklist';
        } else if (foundWhitelist) {
          searchPartner.listType = 'whitelist';
        } else {
          searchPartner.listType = 'searchlist';
        }
      });
      this.setState({ partnerIdentifier, searchedPartners, listType: 'searchlist' });
    } catch (error) {
      this.handelError(error, 'onSearch');
    } finally {
      this.setIsSeachLoading(false);
    }
  };

  addToWhitelist = async (partner) => {
    const { t, user: { userProfileService: { addPartnerToWhitelist } } } = this.props;
    try {
      this.props.setIsFetching(true);
      const whitelistedPartners = await addPartnerToWhitelist(partner.uid);
      this.setState({ searchedPartners: whitelistedPartners.partnerWhitelist, listType: 'whitelist' });
      this.props.setSuccessOrFailMessage(t('addSuccess'));
    } catch (error) {
      this.props.setSuccessOrFailMessage(error);
      this.handelError(error, 'addToWhitelist');
    } finally {
      this.props.setIsFetching(false);
    }
  };

  share = async () => {
    const { t, user: { userProfile }, shareInvite } = this.props;
    shareInvite();
    const userServiceClient = new UserServiceClient(getApiConfig(), fetch, Logger.debug, new AuthTokenBuilder());
    const { link } = await userServiceClient.getInviteFriendsLink();

    if (isWeb()) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.setState({ copyLinkMessage: t('copiedInvite') });
        });
    } else {
      shareOpenInviteLink(t, link, userProfile, new Date());
    }
  };

  render() {
    const {
      isSearchLoading,
      searchedPartners,
      partnerIdentifier,
      isLoading,
      listType,
    } = this.state;
    const {
      t,
      user: {
        userProfile: {
          privateProfile = false,
        },
      },
    } = this.props;
    let appliedPartnerInfoStyle = styles;
    if (Platform.OS === 'web') {
      appliedPartnerInfoStyle = invertColorStyles;
    }
    const isWhiteList = listType === 'whitelist';
    const listIsEmpty = searchedPartners.length <= 0;
    return (
      <ScrollView style={styles.scrollViewContainer}>
        {listIsEmpty
          ? (
            <View style={styles.imageAndTextContainer}>
              <View style={styles.imageContainer}>
                <Image
                  style={styles.groupImage}
                  resizeMode='cover'
                  source={require('../assets/images/gym-group.png')}
                />
              </View>
              <View style={styles.headingTextContainer}>
                <Heading
                  title={t('workoutWithFriends')}
                  style={styles.workoutFriendHeader}
                  headerType={HeadingTypes.HEADING_ONE}
                />
                <Text style={styles.workoutFriendSubText}>
                  {t('inviteAFriendForWorkout')}
                </Text>
              </View>
            </View>
          )
          : (
            <View style={styles.container}>
              <View style={styles.imageAndTextContainer}>
                <PrivacyList
                  t={t}
                  block={this.block}
                  listType={listType}
                  remove={this.remove}
                  isLoading={isLoading}
                  unblock={this.unblock}
                  isPrivate={privateProfile}
                  addToWhitelist={this.addToWhitelist}
                  privacyPartnerList={searchedPartners}
                  onRefresh={() => this.onSearch(partnerIdentifier)}
                  openProfile={this.props.openProfile}
                />
              </View>
            </View>
          )}
        <View>
          <Button
            fontColor={Theme.WHITE}
            containerStyle={{ margin: 10 }}
            buttonType={ButtonTypes.NORMAL}
            title={isWeb() ? this.state.copyLinkMessage : t('inviteFriend')}
            onPress={this.share}
          />
        </View>
      </ScrollView>
    );
  }
}

AllowedPartners.propTypes = {
};

const invertColorStyles = StyleSheet.create({
  partnerInfo: {
    fontFamily: Theme.Custom400,
    color: Theme.WHITE,
  },
  partnerInfoContainer: {
    borderColor: Theme.GREY_MEDIUM,
    borderWidth: 0.5,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
  },
});

const styles = StyleSheet.create({
  scrollViewContainer: {
    display: 'flex',
    height: '100%',
  },
  container: {
    flex: 1,
    height: '100%',
    backgroundColor: 'white',
  },
  partnerInfo: {
    color: Theme.GREY_MEDIUM,

    fontFamily: Theme.Custom400,
    paddingVertical: 15,
  },
  partnerInfoContainer: {
    borderColor: Theme.WHITE,
    borderWidth: 0.5,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
  },
  loadingContainer: {
    backgroundColor: 'white',
    justifyContent: 'center',
  },
  activityIndicator: {
    paddingVertical: 10,
  },
  searchContainer: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 20,
    paddingBottom: 10,
  },
  imageAndTextContainer: {
    width: '100%',
    height: defaultScale(580),
  },
  workoutFriendSubText: {
    padding: 10,
    fontSize: 16,
    fontWeight: '400',
  },
  headingTextContainer: {
    height: defaultScale(80),
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageContainer: {
    width: '100%',
    height: defaultScale(500),
  },
  workoutFriendHeader: {
    padding: 10,
    fontSize: 21,
    fontWeight: '700',
  },
  groupImage: {
    width: '100%',
    height: '100%',
  },
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  showToastMessage: (toast) => dispatch(showToastMessage(toast)),
  toggleWorkoutPartnersModal: (visible) => dispatch(toggleWorkoutPartnersModal(visible)),
  togglePrivacyRemoveConfirmationModal: (visible) => dispatch(togglePrivacyRemoveConfirmationModalVisible(visible)),
  shareInvite: () => dispatch(shareInvite()),
});

AllowedPartners = connect((state) => ({
  isModalVisible: state.ui.workoutPartnersModalVisible,
  isPrivacyRemoveConfirmationModalVisible: state.ui.privacyRemoveConfirmationModalVisible,
}), mapDispatchToProps)(AllowedPartners);
AllowedPartners = translate(['workoutPartners'], { wait: true })(AllowedPartners);
export default UserDataConsumer(AllowedPartners);
